/**
 * exportProforma — Excel export del Simulador Pro-Forma.
 *
 * Genera un workbook profesional (Resumen + Detalle SKU + Términos)
 * listo para enviar como cotización formal a un cliente.
 */
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import type { Incoterm, PresetScope } from "@/lib/expensePresets";
import { DEFAULT_SOBRECOSTOS } from "@/lib/expensePresets";
import { formatTrmDate } from "@/lib/businessDays";

export interface ProformaRow {
  sku: string | null;
  descripcion: string | null;
  cantidad: number | null;
  hs_code: string | null;
  costo_unitario_cop: number | null;
  beneficio_aduanero?: string | null;
  unidad?: string | null;
  peso_bruto_kg?: number | null;
  volumen_cbm?: number | null;
  arancel_pct?: number | null;
  iva_pct?: number | null;
  /** ICO COP total para el ítem. */
  ico_cop?: number | null;
  /** Fase 3 — Costo unitario en línea base (snapshot V3). Si está presente,
   *  el export agrega columnas "Costo Base Unit" y "Δ Unit". */
  costo_base_unitario_cop?: number | null;
}

/** Fila de gasto para la hoja "Desglose de Gastos". */
export interface ProformaGastoRow {
  concepto: string;
  scope: PresetScope | "—";
  moneda: string;
  monto: number;
  base_prorrateo: string;
  incluir_en_base_gravable?: boolean;
}

export interface ExportProformaOptions {
  /** Nombre de la simulación (cabecera del Resumen). */
  nombre: string;
  /** Proveedor "base" del escenario, si lo hubo. */
  proveedor?: string | null;
  /** TRM efectiva utilizada por el motor en el escenario proyectado. */
  trmUsada?: number | null;
  /** Fuente TRM ("aduana" | "mercado"). */
  trmSource?: string | null;
  /** Numero DO base (si es clonado). */
  numeroDoBase?: string | null;
  /** Datos comerciales (con defaults razonables). */
  validezDias?: number;
  incoterm?: Incoterm | string;
  puertoCarga?: string;
  puertoDestino?: string;
  transitoDias?: number;
  condicionesPago?: string;
  notas?: string;
  /** Logística destino (Fase 1 cotización). */
  diasLibresDestino?: number;
  tiempoNacionalizacionDias?: number;
  observaciones?: string;
  sobrecostosPosibles?: string[];
  /** Desglose de gastos (Sheet 4). */
  gastos?: ProformaGastoRow[];
  /** Fase 5B — Fecha de la TRM utilizada (ISO o Date). */
  trmDate?: string | Date | null;
  /** Fase 5B — true si la TRM ya supera el umbral de antigüedad (5 días hábiles). */
  trmOutdated?: boolean;
  /** IVA proyectado total (COP) — recuperable, no se capitaliza al landed. Para flujo de caja. */
  ivaTotal?: number;
  /** Desembolso total estimado (COP) = landed + IVA. Para flujo de caja. */
  desembolsoTotal?: number;
  /** Datos del vendedor. */
  vendedor?: {
    nombre?: string;
    nit?: string;
    email?: string;
    telefono?: string;
  };
}

const sanitizeFilename = (s: string) =>
  (s || "simulacion").replace(/[^a-zA-Z0-9_-]+/g, "_").slice(0, 60);

const fmtCOP = '"$"#,##0;("$"#,##0);"-"';
const fmtPct = "0.00%";
const fmtNum2 = "#,##0.##";
const fmtNum3 = "#,##0.###";

const COLOR_PRIMARY = "FF0F4C81"; // Azul corporativo
const COLOR_HEADER_BG = "FFF3F4F6"; // Gris claro
const COLOR_BORDER = "FFD1D5DB";
const COLOR_BANNER_BG = "FFFFF4CE";
const COLOR_BANNER_FG = "FF8B5A00";

const thinBorder = {
  top: { style: "thin" as const, color: { argb: COLOR_BORDER } },
  bottom: { style: "thin" as const, color: { argb: COLOR_BORDER } },
  left: { style: "thin" as const, color: { argb: COLOR_BORDER } },
  right: { style: "thin" as const, color: { argb: COLOR_BORDER } },
};

export async function exportProformaToExcel(
  rows: ProformaRow[],
  options: ExportProformaOptions,
): Promise<void> {
  const {
    nombre,
    proveedor,
    trmUsada,
    trmSource,
    numeroDoBase,
    validezDias = 30,
    incoterm = "FOB",
    puertoCarga = "Shanghai, China",
    puertoDestino = "Buenaventura, Colombia",
    transitoDias = 35,
    condicionesPago = "T/T 30% adelanto, 70% contra copia BL",
    notas = "Cotización sujeta a cambio sin previo aviso",
    diasLibresDestino,
    tiempoNacionalizacionDias,
    observaciones,
    sobrecostosPosibles,
    gastos,
    trmDate,
    trmOutdated,
    ivaTotal,
    desembolsoTotal,
    vendedor = {
      nombre: "Kargonode Logistics",
      nit: "901.123.456-7",
      email: "sales@kargonode.com",
      telefono: "+57 (601) 555-0000",
    },
  } = options;

  const wb = new ExcelJS.Workbook();
  wb.creator = "Kargonode";
  wb.created = new Date();

  const totalItems = rows.length;
  const totalUnidades = rows.reduce((s, r) => s + Number(r.cantidad ?? 0), 0);
  const totalPeso = rows.reduce(
    (s, r) => s + Number(r.cantidad ?? 0) * Number(r.peso_bruto_kg ?? 0),
    0,
  );
  const totalCBM = rows.reduce(
    (s, r) => s + Number(r.cantidad ?? 0) * Number(r.volumen_cbm ?? 0),
    0,
  );
  const totalCosto = rows.reduce(
    (s, r) => s + Number(r.cantidad ?? 0) * Number(r.costo_unitario_cop ?? 0),
    0,
  );

  const trmLabel =
    trmSource === "aduana"
      ? "Oficial DIM (Final)"
      : trmSource === "mercado"
        ? "Histórica de mercado (Estimada)"
        : "—";

  const fechaEmision = new Date();
  const fechaVencimiento = new Date(fechaEmision);
  fechaVencimiento.setDate(fechaVencimiento.getDate() + validezDias);
  const fmtFecha = (d: Date) =>
    d.toLocaleDateString("es-CO", { day: "2-digit", month: "2-digit", year: "numeric" });

  // ════════════════════════════════════════════════════════════════
  // Sheet 1: RESUMEN
  // ════════════════════════════════════════════════════════════════
  const resumen = wb.addWorksheet("Resumen", {
    pageSetup: { paperSize: 9, orientation: "portrait", fitToPage: true },
  });
  resumen.columns = [{ width: 38 }, { width: 52 }];

  // Banner principal
  resumen.addRow(["KARGONODE — SIMULADOR PRO-FORMA", ""]);
  const titleRow = resumen.getRow(1);
  titleRow.font = { bold: true, size: 16, color: { argb: "FFFFFFFF" } };
  titleRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLOR_PRIMARY } };
  titleRow.alignment = { vertical: "middle", horizontal: "center" };
  titleRow.height = 34;
  resumen.mergeCells("A1:B1");

  resumen.addRow(["COTIZACIÓN OFICIAL", ""]);
  const subTitleRow = resumen.getRow(2);
  subTitleRow.font = { bold: true, size: 11, color: { argb: COLOR_PRIMARY } };
  subTitleRow.alignment = { vertical: "middle", horizontal: "center" };
  subTitleRow.height = 20;
  resumen.mergeCells("A2:B2");

  resumen.addRow(["", ""]); // espaciador

  // Sección: VENDEDOR
  const sectionVendedor = resumen.addRow(["VENDEDOR", ""]);
  sectionVendedor.font = { bold: true, size: 11, color: { argb: "FFFFFFFF" } };
  sectionVendedor.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLOR_PRIMARY } };
  sectionVendedor.alignment = { horizontal: "left", vertical: "middle" };
  sectionVendedor.height = 22;
  resumen.mergeCells(`A${sectionVendedor.number}:B${sectionVendedor.number}`);

  const vendedorRows: Array<[string, string]> = [
    ["Razón social", vendedor.nombre ?? "—"],
    ["NIT", vendedor.nit ?? "—"],
    ["Email", vendedor.email ?? "—"],
    ["Teléfono", vendedor.telefono ?? "—"],
  ];
  vendedorRows.forEach(([k, v]) => resumen.addRow([k, v]));

  resumen.addRow(["", ""]); // espaciador

  // Sección: DATOS COMERCIALES
  const sectionComercial = resumen.addRow(["DATOS COMERCIALES", ""]);
  sectionComercial.font = { bold: true, size: 11, color: { argb: "FFFFFFFF" } };
  sectionComercial.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLOR_PRIMARY } };
  sectionComercial.alignment = { horizontal: "left", vertical: "middle" };
  sectionComercial.height = 22;
  resumen.mergeCells(`A${sectionComercial.number}:B${sectionComercial.number}`);

  const comercialRows: Array<[string, string | number]> = [
    ["Simulación / Referencia", nombre],
    ["Embarque base (clonado)", numeroDoBase ?? "—"],
    ["Proveedor base", proveedor ?? "—"],
    ["Fecha de emisión", fmtFecha(fechaEmision)],
    ["Validez de la cotización", `${validezDias} días (vence ${fmtFecha(fechaVencimiento)})`],
    ["Incoterm", `${incoterm} ${puertoCarga.split(",")[0] ?? ""}`.trim()],
    ["Puerto de carga", puertoCarga],
    ["Puerto de destino", puertoDestino],
    ["Tiempo de tránsito estimado", `${transitoDias} días`],
    ["Condiciones de pago", condicionesPago],
  ];
  comercialRows.forEach(([k, v]) => resumen.addRow([k, v]));

  resumen.addRow(["", ""]); // espaciador

  // Sección: TOTALES
  const sectionTotales = resumen.addRow(["TOTALES DEL ESCENARIO", ""]);
  sectionTotales.font = { bold: true, size: 11, color: { argb: "FFFFFFFF" } };
  sectionTotales.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLOR_PRIMARY } };
  sectionTotales.alignment = { horizontal: "left", vertical: "middle" };
  sectionTotales.height = 22;
  resumen.mergeCells(`A${sectionTotales.number}:B${sectionTotales.number}`);

  const trmRow = resumen.addRow([
    "TRM utilizada (COP/USD)",
    trmUsada && trmUsada > 0 ? Number(trmUsada) : "—",
  ]);
  trmRow.getCell(2).numFmt = "#,##0.00";

  resumen.addRow(["Fuente TRM", trmLabel]);
  resumen.addRow(["Total ítems (SKUs)", totalItems]).getCell(2).numFmt = "#,##0";
  resumen.addRow(["Total unidades", totalUnidades]).getCell(2).numFmt = "#,##0";
  const pesoRow = resumen.addRow(["Peso bruto total (kg)", totalPeso]);
  pesoRow.getCell(2).numFmt = "#,##0.##";
  const cbmRow = resumen.addRow(["Volumen total (CBM)", totalCBM]);
  cbmRow.getCell(2).numFmt = "#,##0.###";
  const costoRow = resumen.addRow(["Costo total nacionalizado (COP) — PROYECTADO", totalCosto]);
  costoRow.getCell(2).numFmt = fmtCOP;
  costoRow.font = { bold: true, size: 12 };
  costoRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLOR_HEADER_BG } };

  // IVA proyectado (recuperable) + Desembolso total — flujo de caja
  const ivaProy = Number(ivaTotal ?? 0);
  const desembolso = Number(desembolsoTotal ?? totalCosto + ivaProy);
  const ivaRow = resumen.addRow(["IVA proyectado (recuperable, COP)", ivaProy]);
  ivaRow.getCell(2).numFmt = fmtCOP;
  ivaRow.font = { italic: true, color: { argb: "FF6B7280" } };
  const desembolsoRow = resumen.addRow(["Desembolso total estimado (COP)", desembolso]);
  desembolsoRow.getCell(2).numFmt = fmtCOP;
  desembolsoRow.font = { bold: true, size: 12 };
  desembolsoRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLOR_HEADER_BG } };

  resumen.addRow(["", ""]);

  // Banner de advertencia
  resumen.addRow(["⚠ ESCENARIO PROYECTADO — NO ES UN COSTO LIQUIDADO", ""]);
  const banner = resumen.lastRow!;
  banner.font = { bold: true, color: { argb: COLOR_BANNER_FG } };
  banner.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLOR_BANNER_BG } };
  banner.alignment = { horizontal: "center", vertical: "middle" };
  banner.height = 24;
  resumen.mergeCells(`A${banner.number}:B${banner.number}`);

  resumen.addRow(["", ""]);

  // Sección: LOGÍSTICA EN DESTINO
  const sectionLog = resumen.addRow(["LOGÍSTICA EN DESTINO", ""]);
  sectionLog.font = { bold: true, size: 11, color: { argb: "FFFFFFFF" } };
  sectionLog.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLOR_PRIMARY } };
  sectionLog.alignment = { horizontal: "left", vertical: "middle" };
  sectionLog.height = 22;
  resumen.mergeCells(`A${sectionLog.number}:B${sectionLog.number}`);

  const logRows: Array<[string, string]> = [
    ["Días libres en destino", typeof diasLibresDestino === "number" ? `${diasLibresDestino} días` : "—"],
    ["Tiempo estimado de nacionalización", typeof tiempoNacionalizacionDias === "number" ? `${tiempoNacionalizacionDias} días` : "—"],
    ["Puerto de descarga", puertoDestino],
  ];
  logRows.forEach(([k, v]) => resumen.addRow([k, v]));

  resumen.addRow(["", ""]);

  // Notas
  const sectionNotas = resumen.addRow(["NOTAS", ""]);
  sectionNotas.font = { bold: true, size: 11, color: { argb: "FFFFFFFF" } };
  sectionNotas.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLOR_PRIMARY } };
  resumen.mergeCells(`A${sectionNotas.number}:B${sectionNotas.number}`);

  const trmDateLabel = trmDate ? formatTrmDate(trmDate) : null;
  const trmWarning = trmOutdated && trmUsada && trmUsada > 0 && trmDateLabel
    ? `La TRM utilizada (${Number(trmUsada).toLocaleString("es-CO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} COP/USD) corresponde a la DIM del embarque base (${trmDateLabel}). Para cotizaciones vigentes, se recomienda actualizar la TRM.`
    : null;
  const notasArr = [
    ...(observaciones ? [observaciones] : []),
    notas,
    ...(trmWarning ? [trmWarning] : []),
    "Los aranceles e IVA son estimados según el HS Code proporcionado.",
    "El costo de flete internacional está incluido en el cálculo del landed cost.",
    "Para detalles de términos y condiciones, ver hoja \"Términos y Condiciones\".",
  ];
  notasArr.forEach((n) => {
    const r = resumen.addRow([`• ${n}`, ""]);
    resumen.mergeCells(`A${r.number}:B${r.number}`);
    r.alignment = { wrapText: true, vertical: "top" };
    r.getCell(1).font = { size: 10, color: { argb: "FF374151" } };
  });

  // Estilo: bordes y label-bold para todo el bloque
  for (let i = 4; i <= resumen.rowCount; i++) {
    const r = resumen.getRow(i);
    r.eachCell((cell, col) => {
      cell.border = thinBorder;
      if (col === 1 && cell.value && !cell.isMerged) {
        cell.font = { bold: true, color: { argb: "FF374151" }, size: 10 };
      }
    });
  }

  // ════════════════════════════════════════════════════════════════
  // Sheet 2: DETALLE SKU
  // ════════════════════════════════════════════════════════════════
  const detalle = wb.addWorksheet("Detalle SKU", {
    views: [{ state: "frozen", ySplit: 1 }],
  });

  // Fase 3 — si alguna fila trae costo base (snapshot), añadimos columnas comparativas.
  const hasBaseline = rows.some(
    (r) => r.costo_base_unitario_cop != null && Number(r.costo_base_unitario_cop) > 0,
  );

  const headers = [
    "SKU",
    "Descripción",
    "Unidad",
    "Cantidad",
    "Peso Unit. (kg)",
    "Vol. Unit. (CBM)",
    "HS Code",
    "Arancel %",
    "IVA %",
    "Régimen",
    "ICO (COP)",
    ...(hasBaseline ? ["Costo Base Unit. (COP)"] : []),
    "Costo Unit. (COP)",
    ...(hasBaseline ? ["Δ Unit. (COP)"] : []),
    "Costo Total (COP)",
  ];
  detalle.addRow(headers);
  const headerRow = detalle.getRow(1);
  headerRow.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
  headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLOR_PRIMARY } };
  headerRow.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
  headerRow.height = 28;

  rows.forEach((r) => {
    const cantidad = Number(r.cantidad ?? 0);
    const costoUnit = Number(r.costo_unitario_cop ?? 0);
    const costoBase = Number(r.costo_base_unitario_cop ?? 0);
    const deltaUnit = costoUnit - costoBase;
    const baseCells = hasBaseline ? [costoBase] : [];
    const deltaCells = hasBaseline ? [deltaUnit] : [];
    detalle.addRow([
      r.sku ?? "—",
      r.descripcion ?? "—",
      r.unidad ?? "UN",
      cantidad,
      Number(r.peso_bruto_kg ?? 0),
      Number(r.volumen_cbm ?? 0),
      r.hs_code ?? "—",
      Number(r.arancel_pct ?? 0) / 100,
      Number(r.iva_pct ?? 0) / 100,
      r.beneficio_aduanero ?? "GENERAL",
      Number(r.ico_cop ?? 0),
      ...baseCells,
      costoUnit,
      ...deltaCells,
      cantidad * costoUnit,
    ]);
  });

  // Anchos dinámicos según presencia de columnas base/delta
  const baseWidth = hasBaseline ? [{ width: 18 }] : [];
  const deltaWidth = hasBaseline ? [{ width: 16 }] : [];
  detalle.columns = [
    { width: 16 }, { width: 42 }, { width: 9 },  { width: 11 },
    { width: 13 }, { width: 14 }, { width: 14 }, { width: 11 },
    { width: 9 },  { width: 14 }, { width: 16 },
    ...baseWidth,
    { width: 18 },
    ...deltaWidth,
    { width: 20 },
  ];

  // Índices dinámicos de columnas COP (1-indexed)
  const colICO = 11;
  const colCostoBase = hasBaseline ? 12 : null;
  const colCostoUnit = hasBaseline ? 13 : 12;
  const colDelta = hasBaseline ? 14 : null;
  const colCostoTotal = hasBaseline ? 15 : 13;

  detalle.getColumn(4).numFmt = "#,##0";
  detalle.getColumn(5).numFmt = fmtNum2;
  detalle.getColumn(6).numFmt = fmtNum3;
  detalle.getColumn(8).numFmt = fmtPct;
  detalle.getColumn(9).numFmt = fmtPct;
  detalle.getColumn(colICO).numFmt = fmtCOP;
  if (colCostoBase) detalle.getColumn(colCostoBase).numFmt = fmtCOP;
  detalle.getColumn(colCostoUnit).numFmt = fmtCOP;
  if (colDelta) detalle.getColumn(colDelta).numFmt = fmtCOP;
  detalle.getColumn(colCostoTotal).numFmt = fmtCOP;

  const dataLastRow = detalle.rowCount;
  const colLetter = (n: number) => String.fromCharCode(64 + n); // 1→A, 2→B, ...
  const totalsCells: Array<string | { formula: string }> = new Array(headers.length).fill("");
  totalsCells[0] = "TOTAL";
  totalsCells[3] = { formula: `SUM(D2:D${dataLastRow})` };
  totalsCells[4] = { formula: `SUMPRODUCT(D2:D${dataLastRow},E2:E${dataLastRow})` };
  totalsCells[5] = { formula: `SUMPRODUCT(D2:D${dataLastRow},F2:F${dataLastRow})` };
  totalsCells[colICO - 1] = { formula: `SUM(${colLetter(colICO)}2:${colLetter(colICO)}${dataLastRow})` };
  if (colCostoBase) totalsCells[colCostoBase - 1] = { formula: `SUMPRODUCT(D2:D${dataLastRow},${colLetter(colCostoBase)}2:${colLetter(colCostoBase)}${dataLastRow})` };
  if (colDelta) totalsCells[colDelta - 1] = { formula: `SUMPRODUCT(D2:D${dataLastRow},${colLetter(colDelta)}2:${colLetter(colDelta)}${dataLastRow})` };
  totalsCells[colCostoTotal - 1] = { formula: `SUM(${colLetter(colCostoTotal)}2:${colLetter(colCostoTotal)}${dataLastRow})` };
  const totalsRow = detalle.addRow(totalsCells);
  totalsRow.font = { bold: true, size: 11 };
  totalsRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLOR_HEADER_BG } };
  totalsRow.getCell(4).numFmt = "#,##0";
  totalsRow.getCell(5).numFmt = fmtNum2;
  totalsRow.getCell(6).numFmt = fmtNum3;
  totalsRow.getCell(colICO).numFmt = fmtCOP;
  if (colCostoBase) totalsRow.getCell(colCostoBase).numFmt = fmtCOP;
  if (colDelta) totalsRow.getCell(colDelta).numFmt = fmtCOP;
  totalsRow.getCell(colCostoTotal).numFmt = fmtCOP;

  // Bordes
  for (let i = 1; i <= detalle.rowCount; i++) {
    detalle.getRow(i).eachCell((cell) => {
      cell.border = thinBorder;
    });
  }

  // Auto-filtro
  detalle.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: dataLastRow, column: headers.length },
  };

  // Alineaciones de cuerpo
  for (let i = 2; i <= dataLastRow; i++) {
    const row = detalle.getRow(i);
    row.getCell(3).alignment = { horizontal: "center" };
    row.getCell(7).alignment = { horizontal: "center" };
    row.getCell(10).alignment = { horizontal: "center" };
  }

  // ════════════════════════════════════════════════════════════════
  // Sheet 3: TÉRMINOS Y CONDICIONES
  // ════════════════════════════════════════════════════════════════
  const terms = wb.addWorksheet("Términos y Condiciones");
  terms.columns = [{ width: 6 }, { width: 110 }];

  terms.addRow(["", "TÉRMINOS Y CONDICIONES DE LA COTIZACIÓN"]);
  const tTitle = terms.getRow(1);
  tTitle.font = { bold: true, size: 14, color: { argb: "FFFFFFFF" } };
  tTitle.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLOR_PRIMARY } };
  tTitle.alignment = { horizontal: "center", vertical: "middle" };
  tTitle.height = 30;
  terms.mergeCells("A1:B1");

  terms.addRow(["", ""]);

  const terminos = [
    `Validez: ${validezDias} días desde la fecha de emisión (${fmtFecha(fechaEmision)}).`,
    `Los precios son ${incoterm} ${puertoCarga}, salvo indicación expresa en contrario.`,
    "Los aranceles e IVA de importación en Colombia son estimados y pueden variar según la clasificación arancelaria definitiva de la DIAN.",
    "El costo de nacionalización (landed cost) es una estimación basada en los parámetros de la simulación; no constituye un costo liquidado.",
    `Condiciones de pago: ${condicionesPago}.`,
    `Tiempo de tránsito estimado: ${transitoDias} días desde ${puertoCarga} hasta ${puertoDestino}, sujeto a disponibilidad de espacio y condiciones del transportista.`,
    "Para proceder con la orden de compra, se requiere la aceptación formal de la cotización.",
    "Esta cotización no constituye una oferta vinculante hasta la emisión de la orden de compra y la confirmación del proveedor.",
    "Cualquier cargo adicional por inspecciones, demoras portuarias, almacenaje o variaciones de TRM serán facturados por separado.",
  ];

  terminos.forEach((t, i) => {
    const r = terms.addRow([`${i + 1}.`, t]);
    r.getCell(1).font = { bold: true, color: { argb: COLOR_PRIMARY } };
    r.getCell(1).alignment = { horizontal: "right", vertical: "top" };
    r.getCell(2).alignment = { wrapText: true, vertical: "top" };
    r.getCell(2).font = { size: 10, color: { argb: "FF1F2937" } };
    r.height = 36;
    r.eachCell((cell) => {
      cell.border = {
        bottom: { style: "hair", color: { argb: COLOR_BORDER } },
      };
    });
  });

  // Sub-sección: POSIBLES SOBRECOSTOS
  terms.addRow(["", ""]);
  const subSob = terms.addRow(["", "POSIBLES SOBRECOSTOS"]);
  subSob.getCell(2).font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
  subSob.getCell(2).fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLOR_PRIMARY } };
  subSob.getCell(2).alignment = { horizontal: "left", vertical: "middle" };
  subSob.height = 22;

  const sobrecostosList =
    sobrecostosPosibles && sobrecostosPosibles.length > 0
      ? sobrecostosPosibles
      : DEFAULT_SOBRECOSTOS;

  sobrecostosList.forEach((s) => {
    const r = terms.addRow(["•", s]);
    r.getCell(1).font = { bold: true, color: { argb: COLOR_PRIMARY } };
    r.getCell(1).alignment = { horizontal: "right", vertical: "top" };
    r.getCell(2).alignment = { wrapText: true, vertical: "top" };
    r.getCell(2).font = { size: 10, color: { argb: "FF1F2937" } };
    r.height = 32;
  });

  terms.addRow(["", ""]);
  const footer = terms.addRow(["", `${vendedor.nombre ?? "Kargonode"} — ${vendedor.email ?? ""} · ${vendedor.telefono ?? ""}`]);
  footer.getCell(2).font = { italic: true, size: 9, color: { argb: "FF6B7280" } };
  footer.getCell(2).alignment = { horizontal: "center" };
  terms.mergeCells(`A${footer.number}:B${footer.number}`);

  // ════════════════════════════════════════════════════════════════
  // Sheet 4: DESGLOSE DE GASTOS
  // ════════════════════════════════════════════════════════════════
  if (gastos && gastos.length > 0) {
    const desg = wb.addWorksheet("Desglose de Gastos", {
      views: [{ state: "frozen", ySplit: 1 }],
    });
    const gHeaders = ["Concepto", "Scope", "Moneda", "Monto", "Base prorrateo", "Suma a base gravable"];
    desg.addRow(gHeaders);
    const gHeader = desg.getRow(1);
    gHeader.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
    gHeader.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLOR_PRIMARY } };
    gHeader.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    gHeader.height = 26;

    const scopeLabel = (s: ProformaGastoRow["scope"]) =>
      s === "origen" ? "Origen" : s === "internacional" ? "Internacional" : s === "destino" ? "Destino" : "—";

    gastos.forEach((g) => {
      desg.addRow([
        g.concepto,
        scopeLabel(g.scope),
        g.moneda,
        Number(g.monto ?? 0),
        g.base_prorrateo,
        g.incluir_en_base_gravable ? "Sí" : "No",
      ]);
    });

    desg.columns = [
      { width: 42 }, { width: 16 }, { width: 10 }, { width: 16 }, { width: 16 }, { width: 22 },
    ];

    const lastRow = desg.rowCount;
    // Format Monto column 4 dynamically per-row based on currency
    for (let i = 2; i <= lastRow; i++) {
      const r = desg.getRow(i);
      const moneda = String(r.getCell(3).value ?? "").toUpperCase();
      r.getCell(4).numFmt = moneda === "COP" ? fmtCOP : "#,##0.00";
      r.getCell(2).alignment = { horizontal: "center" };
      r.getCell(3).alignment = { horizontal: "center" };
      r.getCell(6).alignment = { horizontal: "center" };
    }

    for (let i = 1; i <= lastRow; i++) {
      desg.getRow(i).eachCell((cell) => {
        cell.border = thinBorder;
      });
    }

    desg.autoFilter = { from: { row: 1, column: 1 }, to: { row: lastRow, column: gHeaders.length } };
  }

  // ════════════════════════════════════════════════════════════════
  // Save
  // ════════════════════════════════════════════════════════════════
  const buf = await wb.xlsx.writeBuffer();
  saveAs(
    new Blob([buf], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    `Proforma_${sanitizeFilename(nombre)}_${fechaEmision.toISOString().slice(0, 10)}.xlsx`,
  );
}
